<template>
  <transition name="fade" appear>
    <div
      v-show="isOpen"
      ref="searchModal"
      class="absolute inset-x-0 top-0 z-40"
      @keydown.esc="close"
      role="dialog"
      aria-modal="true"
      aria-labelledby="search-modal-title"
    >
      <h2 id="search-modal-title" class="sr-only">{{ $t('search.modalTitle') }}</h2>
      <Search ref="search" id="search-input-modal" />
    </div>
  </transition>
</template>

<script setup>
import { useSearchStore } from '@/stores/search';
import Search from '@/components/search/Search.vue';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';

const searchStore = useSearchStore();
const search = ref(null);
const searchModal = ref(null);
const previousFocusedElement = ref(null);

const isOpen = computed(() => searchStore.isOpen);
const searchQuery = computed(() => searchStore.searchQuery);

const close = () => searchStore.close();

// Focus trap setup
const { activate, deactivate } = useFocusTrap(searchModal);

watch(isOpen, (value) => {
  if (value) {
    previousFocusedElement.value = document.activeElement;
    document.querySelector('body').classList.add('overflow-hidden');
    nextTick(() => {
      search.value.focusSearchInput();
      activate();
    });
  } else {
    document.querySelector('body').classList.remove('overflow-hidden');
    deactivate();
    if (previousFocusedElement.value && typeof previousFocusedElement.value.focus === 'function') {
      nextTick(() => {
        previousFocusedElement.value.focus();
      });
    }
  }
});

// Watch for search query changes to update the search input when modal is open
watch(searchQuery, () => {
  if (isOpen.value && search.value) {
    nextTick(() => {
      search.value.focusSearchInput();
    });
  }
});

onBeforeUnmount(() => {
  deactivate();
});
</script>

<style>
@reference "~/assets/css/main.css";
.fade-enter-active,
.fade-leave-active {
  transition: opacity 200ms 50ms;
}
.fade-enter,
.fade-leave-to {
  @apply opacity-0;
  transition: opacity 200ms 0ms;
}
</style>
